/**
 * <product-finder> — the two-question diagnosis widget on the homepage.
 *
 * Behaves as a wizard: only the live question shows its options. Answering a
 * step collapses it to the chosen value plus a Change link, and reveals the
 * next step — so the panel never shows two open question sets at once.
 *
 * Every rule's result markup is already in the DOM (rendered by
 * sections/product-finder.liquid and hidden with [hidden]); this component only
 * toggles visibility, so answering costs no network round-trip.
 *
 * State lives in the URL hash as #finder=<material>-<job> so a result can be
 * shared or linked to directly.
 */
if (!customElements.get('product-finder')) {
  customElements.define(
    'product-finder',
    class ProductFinder extends HTMLElement {
      connectedCallback() {
        this.materialButtons = Array.from(this.querySelectorAll('[data-material-option]'));
        this.jobButtons = Array.from(this.querySelectorAll('[data-job-option]'));
        this.jobStep = this.querySelector('[data-step-job]');
        this.result = this.querySelector('[data-result]');
        this.prompt = this.querySelector('[data-prompt]');
        this.rules = Array.from(this.querySelectorAll('[data-rule]'));
        this.materialHeadings = Array.from(this.querySelectorAll('[data-material-heading]'));
        this.globalFallback = this.querySelector('[data-global-fallback]');
        this.resetButton = this.querySelector('[data-reset]');

        this.options = {
          material: this.querySelector('[data-options="material"]'),
          job: this.querySelector('[data-options="job"]'),
        };
        this.answers = {
          material: this.querySelector('[data-answer="material"]'),
          job: this.querySelector('[data-answer="job"]'),
        };
        this.answerValues = {
          material: this.querySelector('[data-answer-value="material"]'),
          job: this.querySelector('[data-answer-value="job"]'),
        };

        this.material = null;
        this.job = null;

        this.materialButtons.forEach((button) => {
          button.addEventListener('click', () => this.chooseMaterial(button.dataset.materialOption));
        });

        this.jobButtons.forEach((button) => {
          button.addEventListener('click', () => this.chooseJob(button.dataset.jobOption));
        });

        Array.from(this.querySelectorAll('[data-edit]')).forEach((button) => {
          button.addEventListener('click', () => this.editStep(button.dataset.edit));
        });

        if (this.resetButton) {
          this.resetButton.addEventListener('click', () => this.reset());
        }

        this.onHashChange = () => this.restoreFromHash();
        window.addEventListener('hashchange', this.onHashChange);

        // A refresh starts the finder over. Arriving on a shared #finder link
        // still restores it — only an explicit reload is treated as "start again".
        if (this.isReload()) {
          this.clearHash();
        } else {
          this.restoreFromHash();
        }
      }

      disconnectedCallback() {
        window.removeEventListener('hashchange', this.onHashChange);
      }

      /* ---- step visibility ---- */

      collapseStep(step, label) {
        if (this.options[step]) this.options[step].hidden = true;
        if (this.answerValues[step]) this.answerValues[step].textContent = label;
        if (this.answers[step]) this.answers[step].hidden = false;
      }

      openStep(step) {
        if (this.options[step]) this.options[step].hidden = false;
        if (this.answers[step]) this.answers[step].hidden = true;
      }

      labelFor(step, value) {
        const attribute = step === 'material' ? 'materialOption' : 'jobOption';
        const list = step === 'material' ? this.materialButtons : this.jobButtons;
        const button = list.filter((el) => el.dataset[attribute] === value)[0];
        return button ? button.textContent.trim() : value;
      }

      /* ---- selection ---- */

      chooseMaterial(material, options) {
        const silent = options && options.silent;
        this.material = material;
        this.job = null;

        this.materialButtons.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.materialOption === material));
        });

        this.collapseStep('material', this.labelFor('material', material));

        // Only offer jobs this material actually has a rule for — never a dead end.
        let firstAvailable = null;
        this.jobButtons.forEach((button) => {
          const materials = (button.dataset.materials || '').split(' ');
          const available = materials.indexOf(material) !== -1;
          button.hidden = !available;
          button.setAttribute('aria-pressed', 'false');
          if (available && !firstAvailable) firstAvailable = button;
        });

        this.openStep('job');
        if (this.jobStep) this.jobStep.hidden = false;

        this.showRules(material, null);
        this.writeHash(options);

        // Keyboard users land on the question that just appeared.
        if (!silent && firstAvailable) firstAvailable.focus();
      }

      chooseJob(job, options) {
        if (!this.material) return;
        const silent = options && options.silent;
        this.job = job;

        this.jobButtons.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.jobOption === job && !button.hidden));
        });

        this.collapseStep('job', this.labelFor('job', job));

        this.showRules(this.material, job);
        this.writeHash(options);

        if (!silent) {
          const heading = this.result.querySelector('.pf__rule:not([hidden]) .pf-card__title a');
          if (heading) heading.focus({ preventScroll: true });
        }
      }

      editStep(step) {
        if (step === 'material') {
          // Changing the material invalidates the job answer beneath it.
          this.job = null;
          this.jobButtons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
          this.openStep('job');
          if (this.jobStep) this.jobStep.hidden = true;
          this.hideAllResults();
          if (this.prompt) this.prompt.hidden = false;
          this.result.classList.remove('pf__result--list');
          this.material = null;
          this.materialButtons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
          this.openStep('material');
          this.clearHash();
          if (this.materialButtons[0]) this.materialButtons[0].focus();
          return;
        }

        this.openStep('job');
        const firstAvailable = this.jobButtons.filter((button) => !button.hidden)[0];
        if (firstAvailable) firstAvailable.focus();
      }

      reset() {
        this.material = null;
        this.job = null;

        this.materialButtons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
        this.jobButtons.forEach((button) => {
          button.setAttribute('aria-pressed', 'false');
          button.hidden = false;
        });

        this.openStep('material');
        this.openStep('job');
        if (this.jobStep) this.jobStep.hidden = true;

        this.hideAllResults();
        if (this.prompt) this.prompt.hidden = false;
        this.result.classList.remove('pf__result--list');

        this.clearHash();

        const first = this.materialButtons[0];
        if (first) first.focus();
      }

      /* ---- results ---- */

      hideAllResults() {
        this.rules.forEach((rule) => {
          rule.hidden = true;
        });
        this.materialHeadings.forEach((heading) => {
          heading.hidden = true;
        });
        if (this.globalFallback) this.globalFallback.hidden = true;
      }

      showRules(material, job) {
        this.hideAllResults();
        if (this.prompt) this.prompt.hidden = true;

        const exact = job
          ? this.rules.filter((rule) => rule.dataset.material === material && rule.dataset.job === job)
          : [];

        if (exact.length) {
          this.result.classList.remove('pf__result--list');
          exact.forEach((rule) => {
            rule.hidden = false;
          });
          return;
        }

        // Between questions, and for stale deep links, show everything for the
        // material rather than an empty panel.
        const forMaterial = this.rules.filter((rule) => rule.dataset.material === material);

        if (forMaterial.length) {
          this.result.classList.add('pf__result--list');
          forMaterial.forEach((rule) => {
            rule.hidden = false;
          });
          const heading = this.materialHeadings.filter((el) => el.dataset.materialHeading === material)[0];
          if (heading) heading.hidden = false;
          return;
        }

        this.result.classList.remove('pf__result--list');
        if (this.globalFallback) this.globalFallback.hidden = false;
      }

      /* ---- hash ---- */

      isReload() {
        try {
          const entries = performance.getEntriesByType('navigation');
          if (entries && entries.length) return entries[0].type === 'reload';
          // Safari < 15 and other older engines.
          return !!(performance.navigation && performance.navigation.type === 1);
        } catch (error) {
          return false;
        }
      }

      writeHash(options) {
        if (options && options.silent) return;
        const value = this.job ? this.material + '-' + this.job : this.material;
        const url = window.location.pathname + window.location.search + '#finder=' + value;
        window.history.replaceState(null, '', url);
      }

      clearHash() {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      restoreFromHash() {
        const match = /[#&]finder=([a-z0-9-]+)/i.exec(window.location.hash);
        if (!match) return;

        const parts = match[1].toLowerCase().split('-');
        const material = parts[0];
        const job = parts[1];

        const validMaterial = this.materialButtons.some((button) => button.dataset.materialOption === material);
        if (!validMaterial) return;

        this.chooseMaterial(material, { silent: true });

        if (job) {
          const validJob = this.jobButtons.some((button) => button.dataset.jobOption === job);
          if (validJob) this.chooseJob(job, { silent: true });
        }
      }
    }
  );
}
